import Constants from "expo-constants";
import { Platform } from "react-native";

import { API_BASE_URL } from "./config";
import { ApiError, decodeApiResponse } from "./response";
import { isBackpressure, parseRetryAfter } from "./retry-after";
import { socketRecovery } from "./socket-recovery";
import i18n from "@/i18n";

/**
 * Release identification, on every request (ADR-029): lets the server answer
 * the store-update gate and makes version adoption observable from traffic
 * alone, with no install identifier.
 *
 * Native only, deliberately. On web a custom header turns every GET into a
 * CORS preflight, and the store-update gate has no meaning in a browser where
 * a deploy updates everyone instantly. Values are fixed for the process
 * lifetime, so they are computed once.
 */
const releaseHeaders: Record<string, string> = {};
if (Platform.OS === "ios" || Platform.OS === "android") {
  releaseHeaders["X-Opncue-Platform"] = Platform.OS;
  const version = Constants.expoConfig?.version;
  if (version) releaseHeaders["X-Opncue-Version"] = version;
  // The native build number (CFBundleVersion / versionCode) — the value the
  // gate actually compares, because marketing versions are not monotonic.
  const build = Constants.nativeBuildVersion;
  if (build) releaseHeaders["X-Opncue-Build"] = build;
}

/** Module-level access token, kept in sync by the auth store. */
let accessToken: string | null = null;
let onUnauthorized: (() => void) | null = null;

/**
 * Called when a request gets a 401. Should attempt to obtain a fresh access
 * token (e.g. via the refresh token) and return it, or return null if the
 * session can no longer be recovered. Kept in sync by the auth store.
 */
let refreshHandler: (() => Promise<string | null>) | null = null;
/** Shared in-flight refresh so concurrent 401s only trigger one refresh. */
let refreshInFlight: Promise<string | null> | null = null;

export function setAccessToken(token: string | null) {
  accessToken = token;
}

export function getAccessToken() {
  return accessToken;
}

export function setUnauthorizedHandler(handler: (() => void) | null) {
  onUnauthorized = handler;
}

export function setRefreshHandler(
  handler: (() => Promise<string | null>) | null,
) {
  refreshHandler = handler;
}

function runRefresh(): Promise<string | null> {
  if (!refreshHandler) return Promise.resolve(null);
  if (!refreshInFlight) {
    refreshInFlight = refreshHandler().finally(() => {
      refreshInFlight = null;
    });
  }
  return refreshInFlight;
}

/**
 * Ceiling on any single request.
 *
 * `fetch` has no timeout of its own: on a device that cannot reach the API —
 * no network, broken DNS, a host that accepts the connection and never answers
 * — it hangs forever rather than rejecting. That is not merely slow, it is
 * fatal at boot, because the auth store awaits `/users/me` before it can leave
 * the "loading" state, so the app sits on the launch screen with nothing in the
 * log to say why. Long enough not to cut off a slow-but-working connection.
 */
const REQUEST_TIMEOUT_MS = 15000;

export class TimeoutError extends Error {
  constructor(public readonly url: string) {
    super(i18n.t("errors.tryAgainInAMoment"));
    this.name = "TimeoutError";
  }
}

// `ApiError` lives in ./response so the decode rules can be executed in tests
// (this module imports expo-constants/react-native/i18n and cannot load outside
// a device runtime). Re-exported here because a dozen callers import it from
// "@/api/client" and that path stays valid.
export { ApiError, NonJsonResponseError } from "./response";

/**
 * The session is over and cannot be recovered: the refresh token was rejected,
 * or the account behind it no longer exists.
 *
 * Thrown *after* `onUnauthorized` has already started signing the user out, so
 * by the time a caller sees it the app is on its way to the welcome screen and
 * there is nothing left to report — every request still in flight will fail the
 * same way. It exists so callers can tell that apart from a request that failed
 * on its own merits, which is worth surfacing. Extends `ApiError` with status
 * 401, so the existing `err instanceof ApiError && err.status === 401` checks
 * keep matching it.
 */
export class SessionExpiredError extends ApiError {
  constructor() {
    super(401, "Unauthorized");
    this.name = "SessionExpiredError";
  }
}

/**
 * The server's refusals for a deactivated account, in the user's language.
 *
 * Done once, here, rather than at each call site. A deactivated account is
 * refused by cues, messages, Small Groups and becoming visible — four features,
 * a dozen screens — and every one of them already shows `err.message`. Mapping
 * centrally means none of them has to know this feature exists, and none of
 * them can forget to.
 *
 * The API sends English prose in `message` too, so an older build still says
 * something sensible; this only replaces it when we recognise the code.
 */
const DEACTIVATED_MESSAGE_KEYS: Record<string, string> = {
  become_visible: "errors.deactivated.becomeVisible",
  send_cue: "errors.deactivated.sendCue",
  send_message: "errors.deactivated.sendMessage",
  join_group: "errors.deactivated.joinGroup",
  create_group: "errors.deactivated.createGroup",
};

/**
 * "You're no longer near X" — a refusal that is a *not yet*, not a no.
 *
 * Localised here rather than printed from the server so the sentence is in the
 * reader's language; the server still sends English in `message` for any client
 * that does not do this.
 */
function localizeOutOfRange(err: unknown): unknown {
  if (!(err instanceof ApiError) || err.status !== 403) return err;
  const body = err.body;
  if (!body || typeof body !== "object") return err;
  const { code, name } = body as { code?: unknown; name?: unknown };
  if (code !== "cue_out_of_range" || typeof name !== "string") return err;
  return new ApiError(err.status, i18n.t("errors.outOfRange", { name }), err.body);
}

/**
 * "Your account has been suspended" — in the reader's language, wherever it
 * arrives.
 *
 * The sign-in screen already classifies this refusal itself, but suspension is
 * now also refused mid-session by the shared social gate: a token stays valid
 * for up to 900s after an account is stopped, and in that window a cue, a
 * message, a group join or turning on Open-to-chat is refused with this code.
 * Without this those screens would print the server's English fallback.
 *
 * Reuses the sign-in string rather than adding a second one. It is the same
 * sentence, and two translated copies of one sentence drift.
 */
function localizeSuspended(err: unknown): unknown {
  if (!(err instanceof ApiError) || err.status !== 403) return err;
  const body = err.body;
  if (!body || typeof body !== "object") return err;
  if ((body as { code?: unknown }).code !== "account_suspended") return err;
  return new ApiError(
    err.status,
    i18n.t("auth.verify.suspendedError"),
    err.body,
  );
}

function localizeDeactivated(err: unknown): unknown {
  if (!(err instanceof ApiError) || err.status !== 403) return err;
  const body = err.body;
  if (!body || typeof body !== "object") return err;
  const { code, action } = body as { code?: unknown; action?: unknown };
  if (code !== "account_deactivated" || typeof action !== "string") return err;
  const key = DEACTIVATED_MESSAGE_KEYS[action];
  if (!key) return err;
  return new ApiError(err.status, i18n.t(key), err.body);
}

type RequestOptions = {
  method?: "GET" | "POST" | "PATCH" | "PUT" | "DELETE";
  body?: unknown;
  auth?: boolean;
  signal?: AbortSignal;
  /**
   * Cookie-backed web auth route (/auth/web/*): sends the HttpOnly refresh
   * cookie and the header the server's WebOriginGuard requires. Only the
   * webAuth API functions set this — ordinary requests must never carry the
   * cookie, which is what confines CSRF to the two routes built for it.
   */
  webAuth?: boolean;
  /** Internal: set after a refresh so we only retry a request once. */
  _retried?: boolean;
};


/**
 * Attach the server's retry window to a decoded error, WITHOUT replacing it.
 *
 * ## The bug this shape exists to avoid
 *
 * The first version of this intercepted 429/503 before `res.text()` and threw a
 * fresh `ApiError` with a generic message and no body. That looked tidy and was
 * a user-facing regression: several product flows answer 429 with a STRUCTURED
 * body that the UI reads to build its copy —
 *
 *   * `OTP_SEND_LIMIT` (auth.service.ts) → `lib/otp-limit.ts`, which requires
 *     `status === 429` AND `body.code`, so a stripped body silently returns
 *     null and the welcome screen falls back to generic copy;
 *   * `CUE_COOLDOWN` (cue-cooldown-refusal.ts) → `lib/cue-cooldown.ts`, same
 *     shape, which is how Home explains when a cue may be sent again;
 *   * verification's `retryAfterSeconds` (age-assurance.service.ts) → the
 *     profile verification entry.
 *
 * A 429 is not one thing. It is both "the platform is shedding" and "you
 * specifically may not do this yet", and those arrive on the same status code.
 * Discarding the body to serve the first meaning breaks every instance of the
 * second.
 *
 * So the decode runs first and owns the message and the body; this only adds a
 * field. Backpressure information is additive, never a replacement.
 */
function withRetryAfter(err: unknown, res: Response): unknown {
  if (!(err instanceof ApiError) || !isBackpressure(err.status)) return err;
  // Mutated rather than rebuilt on purpose: `decodeApiResponse` returns
  // ApiError SUBCLASSES (NonJsonResponseError and friends), and reconstructing
  // would flatten them back to the base type and lose whatever a caller was
  // instanceof-checking for.
  // The RAW ask, not a delay we chose. Jitter and the no-header default are
  // scheduling decisions and belong to the consumer that schedules; baking
  // them in here left a number nobody downstream could interpret. `null`
  // (absent or unparseable) becomes undefined so "the server said nothing" is
  // distinguishable from "the server said zero".
  err.retryAfterMs = parseRetryAfter(res.headers.get("retry-after")) ?? undefined;
  return err;
}

export async function request<T>(
  path: string,
  options: RequestOptions = {},
): Promise<T> {
  const {
    method = "GET",
    body,
    auth = true,
    signal,
    webAuth = false,
    _retried = false,
  } = options;
  const url = `${API_BASE_URL}${path}`;
  const shouldLogAuthStart = __DEV__ && path === "/auth/start";

  const headers: Record<string, string> = {
    Accept: "application/json",
    ...releaseHeaders,
  };
  if (body !== undefined) headers["Content-Type"] = "application/json";
  // `!webAuth` is structural, not stylistic: the cookie routes act on the
  // cookie ALONE, and enforcing that here means no call site can regress
  // into sending a bearer alongside it (review, 2026-09-02).
  if (auth && accessToken && !webAuth) headers.Authorization = `Bearer ${accessToken}`;
  if (webAuth) headers["x-opncue-client"] = "web";

  if (shouldLogAuthStart) {
    console.log("[auth:start] request", {
      method,
      url,
      platform: Platform.OS,
      auth,
      hasBody: body !== undefined,
    });
  }

  let res: Response;
  const startedAt = Date.now();
  let waitingTimer: ReturnType<typeof setTimeout> | undefined;

  // Our own abort controller, so the timeout below can cut a hung request
  // loose. A caller-supplied signal is forwarded into it, which keeps caller
  // cancellation working while still guaranteeing an upper bound.
  const controller = new AbortController();
  let timedOut = false;
  const timeoutTimer = setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, REQUEST_TIMEOUT_MS);
  const abortFromCaller = () => controller.abort();
  signal?.addEventListener("abort", abortFromCaller);

  try {
    if (shouldLogAuthStart) {
      waitingTimer = setTimeout(() => {
        console.warn("[auth:start] still waiting", {
          url,
          platform: Platform.OS,
          durationMs: Date.now() - startedAt,
        });
      }, 8000);
    }
    if (signal?.aborted) controller.abort();
    res = await fetch(url, {
      method,
      headers,
      body: body !== undefined ? JSON.stringify(body) : undefined,
      signal: controller.signal,
      // "include", not "same-origin": the web app and the API are different
      // origins (web.opncue.com -> api.opncue.com). Omitted entirely off the
      // web auth routes so the cookie never rides an ordinary request.
      ...(webAuth ? { credentials: "include" as const } : {}),
    });
  } catch (err) {
    if (shouldLogAuthStart) {
      console.warn("[auth:start] network error", {
        url,
        platform: Platform.OS,
        name: err instanceof Error ? err.name : "UnknownError",
        message: err instanceof Error ? err.message : String(err),
      });
    }
    // Distinguish "we gave up" from "the caller cancelled": callers treat an
    // abort they asked for as routine, and must not show it as a failure.
    if (timedOut) {
      console.warn(
        `[api] ${method} ${url} timed out after ${REQUEST_TIMEOUT_MS}ms`,
      );
      throw new TimeoutError(url);
    }
    throw err;
  } finally {
    clearTimeout(timeoutTimer);
    if (waitingTimer) clearTimeout(waitingTimer);
    signal?.removeEventListener("abort", abortFromCaller);
  }

  // The response arriving proves the network works. If a socket is still
  // transport-dead (socket.io's finite reconnect budget ran out during an
  // outage the app foregrounded straight through), this is the only signal
  // that ever arrives, so it rearms realtime — throttled inside, a no-op
  // whenever everything is connected. EXCEPT under backpressure: a 429/503 is
  // the server saying "come back later" (retry-after.ts), and opening a
  // WebSocket on that answer is the shed client coming straight back.
  if (!isBackpressure(res.status)) socketRecovery.nudge();

  if (shouldLogAuthStart) {
    console.log("[auth:start] response", {
      url,
      status: res.status,
      ok: res.ok,
      durationMs: Date.now() - startedAt,
    });
  }

  if (res.status === 401) {
    // Try to silently refresh the session once before giving up. This keeps
    // the user logged in across access-token expiry until the refresh token
    // is revoked (i.e. they explicitly sign out) or itself expires.
    if (auth && !_retried && refreshHandler) {
      const newToken = await runRefresh();
      if (newToken) {
        return request<T>(path, { ...options, _retried: true });
      }
    }
    if (auth) {
      onUnauthorized?.();
      throw new SessionExpiredError();
    }
    throw new ApiError(401, "Unauthorized");
  }

  const text = await res.text();

  try {
    return decodeApiResponse<T>({
      ok: res.ok,
      status: res.status,
      statusText: res.statusText,
      // After redirects: names who actually answered when it was not us.
      finalUrl: res.url,
      text,
      genericMessage: i18n.t("errors.generic"),
    });
  } catch (err) {
    if (shouldLogAuthStart && err instanceof ApiError) {
      console.warn("[auth:start] api error", {
        url,
        status: err.status,
        message: err.message,
        body: err.body,
      });
    }
    throw localizeOutOfRange(
      localizeDeactivated(localizeSuspended(withRetryAfter(err, res))),
    );
  }
}

/**
 * POST a single image as multipart/form-data.
 *
 * Separate from `request` because that one JSON-encodes its body, and because
 * an upload must NOT set Content-Type by hand — the runtime has to append the
 * multipart boundary itself, and a hand-set header silently breaks the parse on
 * the server.
 *
 * Retries a 401 once through the same refresh path as `request`, so an upload
 * that straddles a token expiry does not lose the picked photo.
 */
export async function uploadImage<T>(
  path: string,
  file: { uri: string; name: string; type: string; file?: Blob },
  _retried = false,
): Promise<T> {
  const form = new FormData();
  if (file.file) {
    // Web: a real File from the picker. The browser sets the part's filename
    // and content type from it.
    form.append("photo", file.file, file.name);
  } else {
    // React Native's FormData takes this shape for file parts; the cast is the
    // standard workaround for the DOM lib's narrower `Blob` typing.
    form.append("photo", {
      uri: file.uri,
      name: file.name,
      type: file.type,
    } as unknown as Blob);
  }

  const headers: Record<string, string> = {
    Accept: "application/json",
    ...releaseHeaders,
  };
  if (accessToken) headers.Authorization = `Bearer ${accessToken}`;

  const res = await fetch(`${API_BASE_URL}${path}`, {
    method: "POST",
    headers,
    body: form,
  });

  // Same rearm-on-proof-of-network as `request`, same backpressure exception.
  if (!isBackpressure(res.status)) socketRecovery.nudge();

  if (res.status === 401) {
    if (!_retried && refreshHandler) {
      const newToken = await runRefresh();
      if (newToken) return uploadImage<T>(path, file, true);
    }
    onUnauthorized?.();
    throw new SessionExpiredError();
  }

  const text = await res.text();

  // Same rule as `request`: an OK response whose body is not JSON did not come
  // from our API, and must not be returned as one.
  return decodeApiResponse<T>({
    ok: res.ok,
    status: res.status,
    statusText: res.statusText,
    finalUrl: res.url,
    text,
    genericMessage: i18n.t("errors.generic"),
  });
}

/**
 * Where to fetch a photo's bytes, and the header that authorises it.
 *
 * Not a URL that grants access on its own — the route re-checks the viewer on
 * every request, so this link is worthless to anyone without the token and
 * stops working the moment the photo is deleted or reported.
 */
export function authedImageSource(path: string): {
  uri: string;
  headers: Record<string, string>;
} {
  return {
    uri: `${API_BASE_URL}${path}`,
    headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
  };
}

/**
 * Fetch an authenticated binary resource, refreshing the session once on 401.
 *
 * `authedImageSource` captures the access token at CALL time, and the web image
 * hook then fetches with that snapshot using a bare `fetch` — so it never got
 * the refresh-and-retry that every `request()` call gets. After a chat sat idle
 * long enough for the token to expire, every photo 401'd and rendered the
 * "no longer available" tombstone until the page was reloaded, which looked
 * exactly like a deleted photo. Coach-voice clips had the same fetch and the
 * same fault: an idle practice session fell back to text and stayed there.
 *
 * This reads the CURRENT token rather than a snapshot, and retries once behind
 * the same shared refresh, so a slow tab recovers on its own.
 *
 * `kind` only names the failure ("image_404", "voice_audio_503"): the status is
 * on the thrown `ApiError` either way, but the two resources fail for different
 * reasons and a log line should say which one it was.
 */
export async function fetchAuthedBlob(
  uri: string,
  kind: "image" | "voice_audio" = "image",
): Promise<Blob> {
  const attempt = async (): Promise<Response> =>
    fetch(uri, {
      headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
    });

  let res = await attempt();
  if (res.status === 401 && refreshHandler) {
    const newToken = await runRefresh();
    // Only worth a second request if the refresh actually produced a token;
    // otherwise the session is genuinely over and the caller shows the
    // tombstone, which is then correct rather than misleading.
    if (newToken) res = await attempt();
  }
  // Same rearm-on-proof-of-network as `request`, same backpressure exception —
  // a long practice or chat-photo session may be the only API traffic there is.
  if (!isBackpressure(res.status)) socketRecovery.nudge();
  if (!res.ok) throw new ApiError(res.status, `${kind}_${res.status}`);
  return res.blob();
}

/**
 * Refresh the session once, returning the new access token, or null when the
 * session cannot be recovered.
 *
 * Exposed for the one authenticated transfer we do not perform with `fetch`:
 * a native coach-audio clip is downloaded by the OS through `expo-file-system`,
 * so it cannot borrow `fetchAuthedBlob`'s retry. It must still share this
 * refresh rather than roll its own, so concurrent 401s rotate the refresh token
 * once between them instead of once each.
 *
 * Deliberately does NOT call `onUnauthorized` when the refresh fails. A media
 * fetch is background work; signing the user out from here would turn a stalled
 * download into a logout, and the next real request reaches that conclusion on
 * its own if the session truly is over.
 */
export function refreshSession(): Promise<string | null> {
  return runRefresh();
}
